// Central registry for the home-page categories and the curated lists of
// suggested searches that open when a category is clicked.
//
// Each entry:
//   slug     – URL segment for /browse/:slug
//   label    – display name
//   icon     – lucide icon component name (resolved by importers)
//   blurb    – one-line description for the browse page hero
//   searches – curated 8 popular Amazon-orderable queries (always start with "Best")
//
// IMPORTANT — clicking a category must NEVER trigger an AI search. It opens
// the Browse page where the user picks one of these suggestions. Only the
// pick triggers an AI call. Categories = exploration; Searches = answers.
import {
  Headphones, Laptop, Home as HomeIcon, ChefHat, Dumbbell, Gamepad2, Camera, Coffee,
} from "lucide-react";

export const CATEGORIES = [
  {
    slug: "audio",
    label: "Audio",
    icon: Headphones,
    blurb: "Speakers, headphones, microphones and everything you listen through.",
    searches: [
      "Best wireless earbuds",
      "Best Bluetooth speaker",
      "Best headphones",
      "Best soundbar",
      "Best microphone",
      "Best DAB radio",
      "Best record player",
      "Best smart speaker",
    ],
  },
  {
    slug: "tech",
    label: "Tech",
    icon: Laptop,
    blurb: "Laptops, monitors, peripherals and the everyday gear that powers your day.",
    searches: [
      "Best laptop for students",
      "Best 4K monitor",
      "Best mechanical keyboard",
      "Best wireless mouse",
      "Best mesh wifi router",
      "Best tablet",
      "Best external SSD",
      "Best USB-C hub",
    ],
  },
  {
    slug: "home",
    label: "Home",
    icon: HomeIcon,
    blurb: "Cleaning, comfort and smart-home essentials for everyday living.",
    searches: [
      "Best cordless vacuum",
      "Best robot vacuum",
      "Best air purifier",
      "Best tower fan",
      "Best electric heater",
      "Best mattress",
      "Best smart bulbs",
      "Best home security camera",
    ],
  },
  {
    slug: "kitchen",
    label: "Kitchen",
    icon: ChefHat,
    blurb: "Cookers, prep gear and small appliances that make the kitchen work.",
    searches: [
      "Best air fryer",
      "Best coffee machine",
      "Best blender",
      "Best toaster",
      "Best stand mixer",
      "Best microwave",
      "Best rice cooker",
      "Best slow cooker",
    ],
  },
  {
    slug: "fitness",
    label: "Fitness",
    icon: Dumbbell,
    blurb: "Wearables, weights and home-gym kit to keep you moving.",
    searches: [
      "Best smartwatch for fitness",
      "Best running shoes",
      "Best adjustable dumbbells",
      "Best exercise bike",
      "Best yoga mat",
      "Best treadmill",
      "Best rowing machine",
      "Best foam roller",
    ],
  },
  {
    slug: "gaming",
    label: "Gaming",
    icon: Gamepad2,
    blurb: "PC and console gear engineered for long sessions and competitive play.",
    searches: [
      "Best gaming headset",
      "Best gaming keyboard",
      "Best gaming mouse",
      "Best gaming monitor",
      "Best gaming chair",
      "Best gaming laptop",
      "Best controller",
      "Best racing wheel",
    ],
  },
  {
    slug: "photo",
    label: "Photo",
    icon: Camera,
    blurb: "Cameras, lenses and gear for capturing better photos and video.",
    searches: [
      "Best action camera",
      "Best mirrorless camera",
      "Best DSLR camera",
      "Best drone for photography",
      "Best tripod",
      "Best ring light",
      "Best photo printer",
      "Best SD card for 4K video",
    ],
  },
  {
    slug: "coffee",
    label: "Coffee",
    icon: Coffee,
    blurb: "Machines, grinders and gear for genuinely great coffee at home.",
    searches: [
      "Best bean-to-cup coffee machine",
      "Best manual espresso machine",
      "Best pod coffee machine",
      "Best French press",
      "Best milk frother",
      "Best coffee grinder",
      "Best moka pot",
      "Best instant coffee",
    ],
  },
];

export const getCategory = (slug) =>
  CATEGORIES.find((c) => c.slug === slug?.toLowerCase());
